import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FaCheckCircle, FaEdit, FaExclamationTriangle, FaFileInvoiceDollar, FaPlus, FaSearch, FaTrash } from "react-icons/fa";
import { aprobarCompra, eliminarCuentaPagar, obtenerComprasPorEmpresa, registrarCompra } from "../../../services/contabilidad/comprasService";
import { validarSunat } from "../../../services/contabilidad/sunatValidationService";
import { obtenerOrdenesCompra, cerrarOC } from "../../../services/contabilidad/logisticaService";
import { formatCurrency, promptConfirm, toNum } from "../utils";


const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const GestionCompras = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [ocs, setOcs] = useState([]);
  const [ocSeleccionada, setOcSeleccionada] = useState(null);


  const [form, setForm] = useState({
    proveedorRuc: "", proveedorRazonSocial: "", tipoComprobante: "FACTURA",
    serie: "", numero: "", fechaEmision: new Date().toISOString().slice(0, 10),
    fechaVencimiento: new Date().toISOString().slice(0, 10), moneda: "PEN",
    tipoCambio: 1, subtotal: 0, igv: 0, total: 0,
    tipoGasto: "GASTO_ADMINISTRATIVO", centroCosto: "ADMINISTRACION",
  });

  const cargarDatos = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      const [comprasData, ocsData] = await Promise.all([
        obtenerComprasPorEmpresa(rucEmpresa),
        obtenerOrdenesCompra(rucEmpresa)
      ]);
      setCompras(comprasData);
      // Solo mostrar OCs aprobadas y que no estén cerradas
      setOcs(ocsData.filter(o => o.estado === "APROBADO"));
    }
    catch (e) { setError("No se pudieron cargar los datos."); }
    finally { setLoading(false); }
  }, [rucEmpresa]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);


  const handleMontoChange = (field, val) => {
    const n = toNum(val);
    const f = { ...form, [field]: n };
    if (field === "subtotal") { f.igv = +(n * 0.18).toFixed(2); f.total = +(n + f.igv).toFixed(2); }
    else if (field === "total") { f.subtotal = +(n / 1.18).toFixed(2); f.igv = +(n - f.subtotal).toFixed(2); }
    setForm(f);
  };

  const handleGuardar = async () => {
    try {
      setProcesando(true); setError("");
      const resSunat = await validarSunat({ rucEmisor: form.proveedorRuc, rucReceptor: rucEmpresa, tipoComprobante: form.tipoComprobante, serie: form.serie, numero: form.numero, fechaEmision: form.fechaEmision, total: form.total, moneda: form.moneda });
      
      const compId = await registrarCompra({ rucEmpresa, compra: { ...form, oc_id: ocSeleccionada?.id || null, estadoSunat: resSunat.estado, sunatMensaje: resSunat.mensaje, sunatFuente: resSunat.fuente, estadoDocumento: "APROBADO", usuarioRegistro: usuario?.correo || usuario?.email || "admin@miboleta.pe" } });
      
      // Si nació de una OC, la cerramos
      if (ocSeleccionada) {
        await cerrarOC(ocSeleccionada.id);
      }

      setSuccess(`Compra registrada. Estado SUNAT: ${resSunat.estado}`);
      setShowForm(false); setOcSeleccionada(null); await cargarDatos();
    } catch (e) { setError(`Error al registrar: ${e.message}`); }
    finally { setProcesando(false); }
  };


  const handleAprobar = async (c) => {
    if (!promptConfirm(`¿Aprobar comprobante ${c.comprobante?.serie}-${c.comprobante?.numero}?`)) return;
    try {
      await aprobarCompra({ rucEmpresa, compraId: c.id, usuarioAprobador: usuario?.correo || usuario?.email || "admin@miboleta.pe" });
      setSuccess("Compra aprobada y asiento contable generado."); await cargarDatos();

    } catch (e) { setError("Error al aprobar compra."); }
    finally { setProcesando(false); }
  };

  const handleEliminar = async (c) => {
    if (!window.confirm(`¿Está seguro de eliminar la compra ${c.comprobante?.serie}-${c.comprobante?.numero}? Esta acción borrará el registro, el asiento y liberará la OC.`)) return;
    try {
      setProcesando(true);
      await eliminarCuentaPagar({ 
        rucEmpresa, 
        cuentaPagarId: c.id, 
        usuario: usuario?.correo || usuario?.email || "admin@miboleta.pe" 
      });
      setSuccess("Compra eliminada correctamente.");
      await cargarDatos();
    } catch (e) {
      setError(`Error al eliminar: ${e.message}`);
    } finally {
      setProcesando(false);
    }
  };

  const handleEditar = (c) => {
    setOcSeleccionada(ocs.find(o => o.id === c.oc_id) || null);
    setForm({
      proveedorRuc: c.proveedor_ruc || "",
      proveedorRazonSocial: c.proveedor_razon_social || "",
      tipoComprobante: c.comprobante?.tipo || "FACTURA",
      serie: c.comprobante?.serie || "",
      numero: c.comprobante?.numero || "",
      fechaEmision: c.comprobante?.fecha_emision || "",
      fechaVencimiento: c.comprobante?.fecha_vencimiento || "",
      moneda: c.comprobante?.moneda || "PEN",
      tipoCambio: c.comprobante?.tipo_cambio || 1,
      subtotal: c.montos?.subtotal || 0,
      igv: c.montos?.igv || 0,
      total: c.montos?.total || 0,
      tipoGasto: c.clasificacion?.tipo_gasto || "GASTO_ADMINISTRATIVO",
      centroCosto: c.clasificacion?.centro_costo || "ADMINISTRACION",
    });
    setShowForm(true);
  };

  if (!rucEmpresa) return <Alert variant="warning">RUC de empresa no disponible.</Alert>;

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible className="border-0 shadow-sm">{success}</Alert>}

      {!showForm ? (
        <>
          {/* Barra de acción */}
          <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col md={1} className="text-center">
                  <div className="rounded-3 p-3 d-inline-block" style={{ ...BTN }}>
                    <FaSearch size={20} />
                  </div>
                </Col>
                <Col md={7}>
                  <h5 className="mb-1 fw-semibold">Registro de Compras</h5>
                  <p className="text-muted mb-0 small">Gestione sus facturas de proveedores y validaciones SUNAT.</p>
                </Col>
                <Col md={4} className="text-md-end mt-3 mt-md-0">
                  <Button className="rounded-pill px-4 border-0 shadow-sm" style={BTN} onClick={() => setShowForm(true)}>
                    <FaPlus className="me-2" /> Nueva Compra
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* KPI cards */}
          <Row className="mb-4 g-3">
            <Col md={4}>
              <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", color: "white", borderRadius: "15px" }}>
                  <FaFileInvoiceDollar size="2rem" className="mb-2" />
                  <h4 className="fw-bold mb-0">{compras.length}</h4>
                  <p className="mb-0 small opacity-75">Total compras</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                  <FaCheckCircle size="2rem" className="mb-2" />
                  <h4 className="fw-bold mb-0">{compras.filter(c => c.estados?.aprobacion === "APROBADO").length}</h4>
                  <p className="mb-0 small opacity-75">Aprobadas</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", color: "white", borderRadius: "15px" }}>
                  <FaExclamationTriangle size="2rem" className="mb-2" />
                  <h4 className="fw-bold mb-0">{compras.filter(c => c.estados?.aprobacion !== "APROBADO").length}</h4>
                  <p className="mb-0 small opacity-75">Pendientes</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabla principal */}
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <div className="table-responsive">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="secondary" /><p className="mt-3 text-muted">Cargando compras...</p></div>
              ) : compras.length === 0 ? (
                <div className="text-center py-5">
                  <FaFileInvoiceDollar size="4rem" className="text-muted opacity-25 mb-3" />
                  <h5 className="text-muted fw-semibold">No hay compras registradas</h5>
                  <p className="text-muted">Las facturas registradas aparecerán aquí.</p>
                </div>
              ) : (
                <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.95rem" }}>
                  <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    <tr>
                      <th className="py-3 px-4 text-secondary fw-semibold">Proveedor</th>
                      <th className="py-3 px-4 text-secondary fw-semibold">Documento</th>
                      <th className="py-3 px-4 text-secondary fw-semibold">Emisión</th>
                      <th className="py-3 px-4 text-secondary fw-semibold text-end">Total</th>
                      <th className="py-3 px-4 text-secondary fw-semibold text-center">SUNAT</th>
                      <th className="py-3 px-4 text-secondary fw-semibold text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compras.map((c) => (
                      <tr key={c.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                        <td className="px-4 py-3">
                          <div className="fw-bold text-dark">{c.proveedor_razon_social}</div>
                          <div className="text-muted" style={{ fontSize: "0.82rem" }}>{c.proveedor_ruc}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge style={{ background: "rgba(107,114,128,0.1)", color: "#374151", border: "1px solid rgba(107,114,128,0.25)", borderRadius: "8px" }} className="px-3 py-2">
                            {c.comprobante?.tipo} {c.comprobante?.serie}-{c.comprobante?.numero}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-muted small">{c.comprobante?.fecha_emision}</td>
                        <td className="px-4 py-3 text-end fw-bold">{formatCurrency(c.montos?.total, c.comprobante?.moneda)}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge pill bg={c.sunat?.estado === "VALIDO" ? "success" : "warning"}>{c.sunat?.estado || "PENDIENTE"}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="d-flex justify-content-center gap-2">
                            {c.estados?.aprobacion === "PENDIENTE" ? (
                                <Button size="sm" className="rounded-pill px-3 border-0" style={BTN} onClick={() => handleAprobar(c)}>Aprobar</Button>
                            ) : (
                                <Badge bg="success" className="p-2 rounded-circle"><FaCheckCircle /></Badge>
                            )}
                            <Button size="sm" variant="outline-primary" className="rounded-circle p-1 border-0" onClick={() => handleEditar(c)} title="Editar">
                                <FaEdit />
                            </Button>
                            <Button size="sm" variant="outline-danger" className="rounded-circle p-1 border-0" onClick={() => handleEliminar(c)} title="Eliminar">
                                <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card>
        </>
      ) : (
        <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", borderBottom: "3px solid #9ca3af" }}>
            <h6 className="mb-0 fw-bold">Nueva Compra</h6>
          </Card.Header>
          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            <div className="mb-4 p-3 bg-white rounded-3 border shadow-sm">
              <Form.Label className="small fw-bold text-primary text-uppercase">Vincular con Orden de Compra (OC)</Form.Label>
              <Form.Select 
                className="border-0 bg-light"
                value={ocSeleccionada?.id || ""}
                onChange={(e) => {
                  const oc = ocs.find(o => o.id === e.target.value);
                  if (oc) {
                    setOcSeleccionada(oc);
                    setForm({
                      ...form,
                      proveedorRuc: oc.proveedor_ruc,
                      proveedorRazonSocial: oc.proveedor_razon_social,
                      moneda: oc.moneda,
                      total: oc.total,
                      subtotal: oc.subtotal,
                      igv: oc.igv
                    });
                  } else {
                    setOcSeleccionada(null);
                  }
                }}
              >
                <option value="">-- Compra directa (Sin OC) --</option>
                {ocs.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.correlativo || `OC-${o.id.slice(-6).toUpperCase()}`} - {o.proveedor_razon_social} ({o.moneda} {o.total})
                  </option>
                ))}
              </Form.Select>
              <small className="text-muted d-block mt-2">
                Seleccione una OC certificada por Logística para auto-completar los datos.
              </small>
            </div>

            <Row className="g-3">

              <Col md={4}>
                <Form.Label className="small fw-bold text-uppercase text-muted">RUC Proveedor</Form.Label>
                <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={form.proveedorRuc} onChange={(e) => setForm({ ...form, proveedorRuc: e.target.value })} />
              </Col>
              <Col md={8}>
                <Form.Label className="small fw-bold text-uppercase text-muted">Razón Social</Form.Label>
                <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={form.proveedorRazonSocial} onChange={(e) => setForm({ ...form, proveedorRazonSocial: e.target.value })} />
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-bold text-uppercase text-muted">Comprobante</Form.Label>
                <Form.Select className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={form.tipoComprobante} onChange={(e) => setForm({ ...form, tipoComprobante: e.target.value })}>
                  <option value="FACTURA">Factura</option><option value="BOLETA">Boleta</option><option value="RH">Recibo Honorarios</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-bold text-uppercase text-muted">Serie</Form.Label>
                <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={form.serie} onChange={(e) => setForm({ ...form, serie: e.target.value })} />
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-bold text-uppercase text-muted">Número</Form.Label>
                <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} />
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-bold text-uppercase text-muted">Moneda</Form.Label>
                <Form.Select className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={form.moneda} onChange={(e) => setForm({ ...form, moneda: e.target.value })}>
                  <option value="PEN">Soles (S/)</option><option value="USD">Dólares ($)</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold text-uppercase text-muted">Total (inc. IGV)</Form.Label>
                <Form.Control type="number" className="border-0 shadow-sm py-2 fw-bold" style={{ borderRadius: "10px", fontSize: "1.1rem" }} value={form.total} onChange={(e) => handleMontoChange("total", e.target.value)} />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold text-uppercase text-muted">Tipo Gasto</Form.Label>
                <Form.Select className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={form.tipoGasto} onChange={(e) => setForm({ ...form, tipoGasto: e.target.value })}>
                  <option value="GASTO_ADMINISTRATIVO">Gasto Administrativo</option><option value="GASTO_VENTAS">Gasto de Ventas</option><option value="COSTO_DIRECTO">Costo Directo</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold text-uppercase text-muted">Centro de Costo</Form.Label>
                <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={form.centroCosto} onChange={(e) => setForm({ ...form, centroCosto: e.target.value })} />
              </Col>
            </Row>
          </Card.Body>
          <Card.Footer className="border-0 bg-light p-3" style={{ borderRadius: "0 0 15px 15px" }}>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="light" className="rounded-pill px-4 fw-semibold text-muted" onClick={() => setShowForm(false)} disabled={procesando}>Cancelar</Button>
              <Button className="rounded-pill px-5 border-0 shadow-sm fw-bold" style={BTN} onClick={handleGuardar} disabled={procesando}>
                {procesando ? <Spinner size="sm" animation="border" /> : "Registrar y Aprobar"}
              </Button>
            </div>
          </Card.Footer>
        </Card>
      )}
    </div>
  );
};

export default GestionCompras;
